import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import et from './locales/et.json'
import en from './locales/en.json'
import fi from './locales/fi.json'

/** UI languages, in the order the switcher shows them. */
export const LOCALES = ['et', 'en', 'fi'] as const
export type Locale = (typeof LOCALES)[number]

const STORAGE_KEY = 'kva-locale'

/**
 * BCP 47 tags for `Intl`. Estonian is the project's own language; English
 * defaults to en-GB so dates read day-first like the other two rather than
 * flipping to month-first halfway through the switcher.
 */
const INTL_TAG: Record<Locale, string> = {
  et: 'et-EE',
  en: 'en-GB',
  fi: 'fi-FI',
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      et: { translation: et },
      en: { translation: en },
      fi: { translation: fi },
    },
    fallbackLng: 'et',
    supportedLngs: LOCALES,
    // Lets a browser reporting `et-EE` or `en-US` resolve onto `et` / `en`.
    nonExplicitSupportedLngs: true,
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    // React escapes for us; letting i18next do it too would double-encode.
    interpolation: { escapeValue: false },
  })

/** The active locale, always one of LOCALES (never a region-tagged variant). */
export function currentLocale(): Locale {
  const base = i18n.resolvedLanguage ?? i18n.language ?? 'et'
  return (LOCALES as readonly string[]).includes(base) ? (base as Locale) : 'et'
}

export function intlTag(locale: Locale = currentLocale()): string {
  return INTL_TAG[locale]
}

/**
 * Translate a key assembled at runtime from a backend value — a pipeline stage,
 * a job kind, a review status.
 *
 * These arrive as strings the type system cannot check, and the backend can
 * grow a new one at any time. Falling back to the raw value means an unknown
 * stage shows as `rerank` rather than as a missing-key placeholder, which is
 * ugly but still tells the reader what is happening.
 */
export function translateEnum(prefix: string, value: string, fallback = value): string {
  const key = `${prefix}.${value}`
  return i18n.exists(key) ? i18n.t(key as never) : fallback
}

// Assistive tech and the browser's own hyphenation both key off `lang`, so it
// has to follow the switcher rather than stay at whatever index.html declared.
// The tab title comes along: it is how the app is identified in a row of tabs
// and in browser history, and index.html can only ever hardcode one language.
const applyDocumentChrome = () => {
  document.documentElement.lang = currentLocale()
  document.title = i18n.t('app.documentTitle')
}
applyDocumentChrome()
i18n.on('languageChanged', applyDocumentChrome)

export default i18n
